import { BarController, BarElement, CategoryScale, Chart, LineController, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js';
import { isMobileView, lc, lreplace } from '@utils/toolbox';
import { dateAddWeeks, dateGetISOWeek } from '@utils/dateFns';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, BarController, BarElement, Tooltip);

const BRAND = '#31527A';
const GRID = '#D6E0F1';
// cheaper than last year reads green, pricier reads red
const CHEAPER = '#34AD2D';
const PRICIER = '#E83151';

// shared white rounded tooltip used by both graph styles
const tooltipStyle = {
	boxPadding: 5,
	backgroundColor: '#fff',
	bodyColor: BRAND,
	titleColor: BRAND,
	borderColor: BRAND,
	borderWidth: 1,
	cornerRadius: 9,
	titleAlign: 'center',
	padding: 10,
	bodyFont: { size: 14 },
	titleFont: { size: 14 }
};

// how far the page can scroll before a stale (touch-triggered, fixed-position) tooltip is hidden
const TOOLTIP_SCROLL_HIDE_THRESHOLD = 40;
let tooltipShowScrollY = null;

function getOrCreateTooltipEl () {
	let el = document.getElementById('pricechart-tooltip');
	if (!el) {
		el = document.createElement('div');
		el.id = 'pricechart-tooltip';
		el.className = 'pricechart-tooltip';
		document.body.appendChild(el);

		if (isMobileView()) {
			window.addEventListener('scroll', () => {
				if (tooltipShowScrollY === null || el.style.opacity !== '1') {
					return;
				}
				if (Math.abs(window.scrollY - tooltipShowScrollY) > TOOLTIP_SCROLL_HIDE_THRESHOLD) {
					el.style.opacity = '0';
					tooltipShowScrollY = null;
				}
			}, { passive: true });
		}
	}
	return el;
}

function externalTooltip (context) {
	const { tooltip } = context;
	const el = getOrCreateTooltipEl();

	if (tooltip.opacity === 0) {
		el.style.opacity = '0';
		tooltipShowScrollY = null;
		return;
	}

	const title = tooltip.title?.[0] ?? '';
	const items = (tooltip.body ?? []).map((b, i) => {
		const line = b.lines[0] ?? '';
		const ds = tooltip.dataPoints[i]?.dataset ?? {};
		const color = ds.borderColor || ds.backgroundColor || BRAND;
		return { line, color };
	}).filter(item => item.line);

	el.innerHTML = `<div class="title">${title}</div>`
		+ items.map(({ line, color }) =>
			`<div class="line">`
			+ `<span class="color-mark" style="background:${color}"></span>`
			+ `<span>${line}</span>`
			+ `</div>`
		).join('');

	el.style.opacity = '1';
	tooltipShowScrollY = window.scrollY;

	const rect = context.chart.canvas.getBoundingClientRect();
	const elW = el.offsetWidth || 160;
	const caretX = rect.left + tooltip.caretX;
	const caretY = rect.top + tooltip.caretY;
	const x = caretX + elW + 16 > window.innerWidth ? caretX - elW - 12 : caretX + 12;

	el.style.left = `${x}px`;
	el.style.top = `${caretY}px`;
	el.style.transform = 'translateY(-50%)';
}

/** renders a multi-city line graph into a container element, one dataset per city */
export function renderMultiCityLineGraph (container, datasets, { reverse = false } = {}) {
	if (!container || !datasets.length) {
		return null;
	}

	const labels = datasets[0].points.map(p => p.shortLabel ?? p.label);
	const canvas = document.createElement('canvas');
	container.replaceChildren(canvas);

	return new Chart(canvas, {
		type: 'line',
		data: {
			labels,
			datasets: datasets.map(ds => ({
				label: ds.cityName,
				data: ds.points.map(p => p.avgPrice),
				borderColor: ds.color,
				pointBackgroundColor: ds.color,
				borderWidth: 4,
				tension: 0.3,
				pointRadius: 3,
				pointHoverRadius: 9,
				pointHitRadius: 14,
				fill: false,
				spanGaps: true
			}))
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: datasets.length > 1, labels: { color: BRAND, font: { size: 13 } } },
				tooltip: {
					enabled: false,
					external: externalTooltip,
					callbacks: {
						title: tooltipItems => datasets[0].points[tooltipItems[0].dataIndex].label,
						label: (tooltipItem) => {
							const ds = datasets[tooltipItem.datasetIndex];
							const value = ds.points[tooltipItem.dataIndex].avgPrice;
							return value ? `${ds.cityName}: ${lc(value)}` : '';
						}
					}
				}
			},
			scales: {
				x: {
					reverse,
					grid: { display: false },
					ticks: { color: BRAND, font: { size: 13 }, padding: 6, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 }
				},
				y: {
					grace: '8%',
					border: { display: false },
					grid: { color: GRID },
					ticks: { color: BRAND, font: { size: 13 }, maxTicksLimit: 5, padding: 8, callback: value => lc(value) }
				}
			}
		}
	});
}

/** renders a multi-city year comparison bar graph, one grouped bar cluster per month */
export function renderMultiCityYearComparisonGraph (container, datasets) {
	if (!container || !datasets.length) {
		return null;
	}

	const labels = datasets[0].points.map(p => p.shortLabel ?? p.label);
	const canvas = document.createElement('canvas');
	container.replaceChildren(canvas);

	const changes = datasets.flatMap(ds => ds.points.map(p => Math.abs(p.pctChange)).filter(Number.isFinite));
	const zeroHeadroom = Math.max(...changes, 1) * 0.15;

	return new Chart(canvas, {
		type: 'bar',
		data: {
			labels,
			datasets: datasets.map(ds => ({
				label: ds.cityName,
				data: ds.points.map(p => p.pctChange),
				backgroundColor: ds.color,
				hoverBackgroundColor: ds.color,
				borderRadius: 4,
				maxBarThickness: 32
			}))
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: datasets.length > 1, labels: { color: BRAND, font: { size: 13 } } },
				tooltip: {
					enabled: false,
					external: externalTooltip,
					callbacks: {
						title: tooltipItems => datasets[0].points[tooltipItems[0].dataIndex].label,
						label: (tooltipItem) => {
							const ds = datasets[tooltipItem.datasetIndex];
							const point = ds.points[tooltipItem.dataIndex];
							if (point.pctChange === null) {
								return '';
							}
							return `${ds.cityName}: ${formatPercent(point.pctChange, 1)}`;
						}
					}
				}
			},
			scales: {
				x: {
					grid: { display: false },
					ticks: { color: BRAND, font: { size: 13 }, padding: 6, maxRotation: 0, autoSkip: false }
				},
				y: {
					grace: '15%',
					suggestedMin: -zeroHeadroom,
					suggestedMax: zeroHeadroom,
					border: { display: false },
					grid: { color: context => context.tick.value === 0 ? BRAND : GRID },
					ticks: { color: BRAND, font: { size: 13 }, maxTicksLimit: 8, padding: 8, callback: value => formatPercent(value) }
				}
			}
		}
	});
}

/** shared by the destination (city + country) and city-to-city entry points */
export function mountPriceGraphs () {
	['priceGraph', 'weekdayGraph', 'leadTimeGraph', 'yearComparisonGraph', 'popularityComparisonGraph'].forEach((graphId) => {
		const element = document.getElementById(graphId);
		const data = globalThis.js_params?.[`${graphId}Data`];

		if (!element || !data) {
			return;
		}

		if (data.unit === 'percent') {
			renderYearComparisonGraph(element, data, { popularity: graphId === 'popularityComparisonGraph' });
		} else {
			// lead time reads right-to-left: furthest ahead on the left down to departure on the right
			const leadTime = graphId === 'leadTimeGraph';
			renderPriceGraph(element, data, { reverse: leadTime, extraLabel: leadTime ? bookNowLine : null });
		}
	});
}

export function bookNowLine (point) {
	return lreplace('global.leadTime.bookNowTooltip', {
		bookWeek: dateGetISOWeek(new Date()),
		travelWeek: dateGetISOWeek(dateAddWeeks(new Date(), Number(point.shortLabel)))
	});
}

function renderPriceGraph (container, data, { reverse = false, extraLabel = null } = {}) {
	const points = data.points;
	const canvas = document.createElement('canvas');
	container.append(canvas);

	return new Chart(canvas, {
		type: 'line',
		data: {
			labels: points.map(point => point.shortLabel ?? point.label),
			datasets: [{
				label: data.cityName,
				data: points.map(point => point.avgPrice),
				borderColor: BRAND,
				pointBackgroundColor: BRAND,
				borderWidth: 2,
				tension: 0.3,
				pointRadius: 3,
				pointHoverRadius: 9,
				pointHitRadius: 14,
				fill: false,
				spanGaps: true
			}]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			// no left padding so the y-axis labels line up with the title text
			layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
			plugins: {
				legend: { display: false },
				tooltip: {
					...tooltipStyle,
					callbacks: {
						title: tooltipItems => points[tooltipItems[0].dataIndex].label,
						label: (tooltipItem) => {
							const point = points[tooltipItem.dataIndex];

							if (!point.avgPrice) {
								return '';
							}

							return extraLabel ? [lc(point.avgPrice), extraLabel(point)] : lc(point.avgPrice);
						}
					}
				}
			},
			scales: {
				x: {
					reverse,
					grid: { display: false },
					title: { display: !!data.xAxisTitle, text: data.xAxisTitle, color: BRAND, font: { size: 16 } },
					// keep labels horizontal and let chart.js thin them out (the lead time graph has ~25)
					ticks: { color: BRAND, font: { size: 13 }, padding: 6, maxRotation: 0, autoSkip: true, autoSkipPadding: 8 }
				},
				y: {
					grace: '8%',
					border: { display: false },
					grid: { color: GRID },
					ticks: { color: BRAND, font: { size: 13 }, maxTicksLimit: 5, padding: 8, callback: value => lc(value) }
				}
			}
		}
	});
}

const zeroBaselinePlugin = {
	id: 'zeroBaseline',
	afterDatasetsDraw (chart) {
		const y = Math.floor(chart.scales.y.getPixelForValue(0)) + 0.5;
		const { ctx, chartArea } = chart;

		ctx.save();
		ctx.lineWidth = 1;
		ctx.strokeStyle = BRAND;
		ctx.beginPath();
		ctx.moveTo(chartArea.left, y);
		ctx.lineTo(chartArea.right, y);
		ctx.stroke();
		ctx.restore();
	}
};

// signed percent using the market's decimal symbol like lc does, e.g. -4 => "-4%", 3.2 => "+3,2%"
function formatPercent (value, decimals = 0) {
	const rounded = Number(value.toFixed(decimals));
	const decimalSymbol = globalThis.js_params?.defaultCurrencyFormating?.decimalSymbol ?? ',';
	return `${rounded > 0 ? '+' : ''}${rounded.toString().replace('.', decimalSymbol)}%`;
}

// bar chart of relative price or popularity changes vs last year, diverging around zero.
export function renderYearComparisonGraph (container, data, { popularity = false, rotateLabels = false, clampToZero = false, visibleCount = 0 } = {}) {
	const points = data.points;
	const values = points.map(point => point.pctChange).filter(Number.isFinite);
	const [rangeMin, rangeMax] = [Math.min(...values, 0), Math.max(...values, 0)];
	// air past the zero line even when every bar points the same way (grace never extends a bound sitting at 0)
	const zeroHeadroom = Math.max(Math.abs(rangeMin), Math.abs(rangeMax), 1) * 0.15;
	// one-directional graphs (top risers/fallers) pin the axis at the zero line instead
	const pinMin = clampToZero && rangeMin >= 0;
	const pinMax = clampToZero && rangeMax <= 0;
	// popularity has no good/bad direction, so its bars stay in the brand blue
	const barColors = popularity ? BRAND : points.map(point => point.pctChange <= 0 ? CHEAPER : PRICIER);
	const canvas = document.createElement('canvas');
	container.append(canvas);

	return new Chart(canvas, {
		type: 'bar',
		plugins: clampToZero ? [zeroBaselinePlugin] : [],
		data: {
			labels: points.map(point => point.shortLabel ?? point.label),
			datasets: [{
				label: data.cityName,
				data: points.map(point => point.pctChange),
				backgroundColor: barColors,
				// chart.js shades hovered bars by default, keep them still
				hoverBackgroundColor: barColors,
				borderRadius: 4,
				maxBarThickness: 48
			}]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
			plugins: {
				legend: { display: false },
				tooltip: {
					...tooltipStyle,
					callbacks: {
						title: tooltipItems => points[tooltipItems[0].dataIndex].label,
						label: (tooltipItem) => {
							const point = points[tooltipItem.dataIndex];

							if (point.pctChange === null) {
								return '';
							}

							if (popularity) {
								return formatPercent(point.pctChange, 1);
							}

							return [
								formatPercent(point.pctChange, 1),
								`${data.thisYearLabel}: ${lc(point.currentPrice)}`,
								`${data.lastYearLabel}: ${lc(point.previousPrice)}`
							];
						}
					}
				}
			},
			scales: {
				x: {
					min: visibleCount ? 0 : undefined,
					max: visibleCount ? visibleCount - 1 : undefined,
					grid: { display: false },
					ticks: { color: BRAND, font: { size: 13 }, padding: 6, maxRotation: rotateLabels ? 60 : 0, autoSkip: false }
				},
				y: {
					grace: '15%',
					suggestedMin: pinMin ? undefined : Math.min(rangeMin, -zeroHeadroom),
					suggestedMax: pinMax ? undefined : Math.max(rangeMax, zeroHeadroom),
					min: pinMin ? 0 : undefined,
					max: pinMax ? 0 : undefined,
					beginAtZero: pinMin || pinMax,
					border: { display: false },
					grid: {
						// emphasise the zero baseline the bars diverge from
						color: context => context.tick.value === 0 ? BRAND : GRID,
						lineWidth: context => context.tick.value === 0 && clampToZero ? 0 : 1
					},
					// enough ticks that the scale can stay one nice step above the tallest bar instead of doubling
					ticks: { color: BRAND, font: { size: 13 }, maxTicksLimit: 8, padding: 8, callback: value => formatPercent(value) }
				}
			}
		}
	});
}
